// React Dependencies
import React, { useMemo, useState, useRef } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import MaterialTable from 'material-table';
import { toast } from 'react-toastify';
import { Button } from '@material-ui/core';
import { Settings as SettingsIcon } from '@material-ui/icons';
import { Link } from 'react-router-dom';

// Internal Dependencies
import TrailItemDialog from './TrailItemsDialog';
import TrailItemDelete from './TrailItemDelete';
import OverLoading from '../../../global/OverLoading';
import { Status } from '../../../../common/constants';

const TrailItems = ({
  match, onFetchTableData, type, linkPathGen, linkedIdKey, onUpdateService, onFetchLeftTransferList, onFetchRightTransferList,
}) => {
  const { pageId } = match.params;

  const tableRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [isDeactivated, setIsDeactivated] = useState(false);

  const fetchTableData = (query) => async (resolve) => {
    try {
      setIsLoading(true);
      query.pageNo = query.page;
      const { page } = query;
      const queryFilters = Object.fromEntries(query.filters.map((filters) => [filters.column.field, filters.value]));
      const result = await onFetchTableData({ ...query, ...queryFilters, type });
      setIsDeactivated(result.status === Status.Deactivated);

      resolve({ data: result.rows, page, totalCount: result.totalCount });
    } catch (err) {
      toast.error(err.message);
      resolve({ data: [], page: 0, totalCount: 0 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (ids) => {
    try {
      setIsLoading(true);
      await onUpdateService(ids);
      tableRef.current.onQueryChange();
      setIsManageModalOpen(false);
      toast.success('Updated trail items.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const columns = useMemo(() => [
    {
      title:     'ID',
      filtering: true,
      editable:  'never',
      field:     'id',
      cellStyle: { width: '10%' },
    },
    {
      title:     `${type} ID`,
      filtering: true,
      editable:  'never',
      field:     linkedIdKey,
      cellStyle: { width: '30%' },
      render:    (row) => (
        <Link to={linkPathGen(row[linkedIdKey])} target="_blank">
          {row[linkedIdKey]}
        </Link>
      ),
    },
    {
      title:     'Title',
      filtering: true,
      editable:  'never',
      field:     'title',
      cellStyle: { width: '30%' },
    },
    {
      title:     'Brand',
      filtering: true,
      editable:  'never',
      field:     'brand',
      cellStyle: { width: '20%' },
    },
    {
      title:     'Actions',
      filtering: false,
      editable:  'never',
      cellStyle: { width: '10%' },
      render:    (row) => (
        <TrailItemDelete
          disabled={isDeactivated}
          pageId={pageId}
          trailItemId={row.id}
          onRefresh={() => tableRef.current.onQueryChange()}
          onLoading={() => setIsLoading(true)}
          onLoadingEnd={() => setIsLoading(false)}
        />
      ),
    },
  ], [isDeactivated]);

  return (
    <>
      {isLoading && <OverLoading />}
      <Button
        onClick={() => setIsManageModalOpen(true)}
        disabled={isDeactivated}
        variant="contained"
        color="primary"
        size="medium"
        className="mb-3"
        startIcon={<SettingsIcon />}
      >
        {`Manage ${type} Trail Items`}
      </Button>

      <MaterialTable
        tableRef={tableRef}
        columns={columns}
        isLoading={isLoading}
        components={{ Toolbar: () => null }}
        options={{ filtering: true, search: false, debounceInterval: 500, emptyRowsWhenPaging: false }}
        data={(query) => new Promise(fetchTableData(query))}
      />

      {/* Transfer list modal */}
      {isManageModalOpen && (
        <TrailItemDialog
          open={isManageModalOpen}
          onClose={() => setIsManageModalOpen(false)}
          handleSave={handleSave}
          idKey={linkedIdKey}
          context={type}
          fetchLeft={(params) => onFetchLeftTransferList(params)}
          fetchRight={() => onFetchRightTransferList()}
        />
      )}
    </>
  );
};

TrailItems.propTypes = {
  match:                    PropTypes.shape(Object).isRequired,
  type:                     PropTypes.string.isRequired,
  linkedIdKey:              PropTypes.string.isRequired,
  linkPathGen:              PropTypes.func.isRequired,
  onUpdateService:          PropTypes.func.isRequired,
  onFetchTableData:         PropTypes.func.isRequired,
  onFetchLeftTransferList:  PropTypes.func.isRequired,
  onFetchRightTransferList: PropTypes.func.isRequired,
};

export default TrailItems;
